import React, { useRef, useEffect } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import { makeStyles } from "@material-ui/core";
import { MediaFileType } from "../utils/constants";
import { Mic } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
	videoWrapper: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		display: "flex",
		overflow: "hidden",
		"--plyr-color-main": "rgba(0, 0, 0, .05)",
		"--plyr-control-icon-size": "0.75rem",
		"--plyr-control-spacing": "0.5rem",
	},
	video: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},

	audioWrapper: {
		position: "absolute",
		bottom: 0,
		left: 0,
		padding: "0.25rem",
		backgroundColor: "rgba(0, 0, 0, 0.2)",
		"--plyr-color-main": "rgba(0, 0, 0, 0.6)",
		"--plyr-control-icon-size": "0.75rem",
		"--plyr-control-spacing": "0.5rem",
		"--plyr-audio-controls-background": "transparent",
		"--plyr-audio-control-color": "#333333",
		width: "100%",
	},
	audio: {
		width: "100%",
	},

	audioIconContainer: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "100%",
		height: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: theme.palette.grey[200],
	},
	audioIcon: {
		fontSize: 32,
		color: theme.palette.grey[600],
	},

	"@global": {
		".plyr": {
			minWidth: 0,
		},
		".plyr--video": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
		},
		".plyr--audio .plyr__controls": {
			padding: 0,
		},
		".plyr__control svg": {
			width: "0.75rem",
			height: "0.75rem",
		},
		".plyr--audio .plyr__control:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.1)",
		},
	},
}));

const MediaPlayer = ({
	file,
	limitedControls,
	autoplay = false,
	onLoadMedia,
	style,
}) => {
	const classes = useStyles();
	const mediaRef = useRef(null);
	const playerRef = useRef(null);
	const isAudio = file.mediaFileType === MediaFileType.AUDIO;

	useEffect(() => {
		if (mediaRef.current) {
			let controls;

			if (isAudio) {
				controls = ["play", "current-time"];
			} else {
				controls = limitedControls
					? ["play", "fullscreen"]
					: ["play", "progress", "mute", "download", "fullscreen"];
			}

			playerRef.current = new Plyr(mediaRef.current, {
				controls,
				autoplay,
				clickToPlay: !isAudio,
			});

			return () => {
				if (playerRef.current) {
					playerRef.current.destroy();
				}
			};
		}
	}, [limitedControls, autoplay, isAudio]);

	if (isAudio) {
		return (
			<div style={style}>
				<div className={classes.audioIconContainer}>
					<Mic className={classes.audioIcon} />
				</div>

				<div className={classes.audioWrapper}>
					<audio
						ref={mediaRef}
						src={file.extraSmallContent || file.originalContent}
						className={classes.audio}
						controls
						onLoadedData={onLoadMedia}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={classes.videoWrapper} style={style}>
			<video
				ref={mediaRef}
				src={file.extraSmallContent || file.originalContent}
				className={classes.video}
				controls
				onLoadedData={onLoadMedia}
			/>
		</div>
	);
};

export default React.memo(MediaPlayer);
